import { Box, Grid, Stack, Paper } from "@mui/material";
import { FC } from "react";
import { LayoutProps } from "../types";
import ImageBlock from "../blocks/image-block";
import HeaderBlock from "../blocks/header-block";
import ContentBlock from "../blocks/content-block";
import StatsBlock from "../blocks/stats-block";
import ActionsBlock from "../blocks/actions-block";
import MetaBlock from "../blocks/meta-block";

const FeaturedLayout: FC<LayoutProps> = ({
  blocks,
  imagePosition,
  spacing,
  theme,
  containerProps,
  showBorder = true,
  showBoxShadow = true,
  disableHover = false,
}) => {
  const getSpacingValue = () => {
    switch (spacing) {
      case "compact":
        return 3;
      case "spacious":
        return 8;
      default:
        return 6;
    }
  };

  const getElevation = () => {
    if (!showBoxShadow) return 0;
    switch (theme) {
      case "haianh":
        return 3;
      case "nova-press":
        return 2;
      default:
        return 1;
    }
  };

  const getContainerStyles = () => {
    const baseStyles = {
      position: "relative" as const,
      width: "100%",
      borderRadius: theme === "haianh" ? "16px" : "12px",
      overflow: "hidden",
    };

    const borderStyles = showBorder
      ? {}
      : {
          border: "none",
          boxShadow: "none",
        };

    const hoverStyles = !disableHover
      ? {
          transition: "all 0.3s ease",
          "&:hover": {
            transform: "translateY(-4px)",
            ...(showBoxShadow && {
              boxShadow:
                theme === "haianh"
                  ? "0 12px 32px rgba(255, 193, 7, 0.15)"
                  : "0 8px 24px rgba(0,0,0,0.12)",
            }),
          },
        }
      : {};

    return {
      ...baseStyles,
      ...borderStyles,
      ...hoverStyles,
      ...containerProps?.sx,
    };
  };

  const isImageLeft = imagePosition === "left";
  const hasImage = !!blocks.image;

  return (
    <Paper elevation={getElevation()} sx={getContainerStyles()} {...containerProps}>
      {/* Badge removed - use images with embedded badges instead */}

      <Box sx={{ padding: getSpacingValue() }}>
        <Grid container spacing={getSpacingValue()} alignItems="center">
          {/* Image Section */}
          {hasImage && (
            <Grid
              item
              xs={12}
              lg={4}
              order={{
                xs: 1,
                lg: isImageLeft ? 1 : 2,
              }}
            >
              <ImageBlock image={blocks.image!} theme={theme} />
            </Grid>
          )}

          {/* Content Section */}
          <Grid
            item
            xs={12}
            lg={hasImage ? 8 : 12}
            order={{
              xs: 2,
              lg: isImageLeft ? 2 : 1,
            }}
          >
            <Stack spacing={4} alignItems="flex-start">
              {/* Header */}
              {blocks.header && <HeaderBlock header={blocks.header} theme={theme} />}

              {/* Content */}
              {blocks.content && <ContentBlock content={blocks.content} theme={theme} />}

              {/* Stats - Special layout for featured cards */}
              {blocks.stats && (
                <Box sx={{ width: "100%" }}>
                  <StatsBlock stats={blocks.stats} theme={theme} />
                </Box>
              )}

              {/* Meta */}
              {blocks.meta && <MetaBlock meta={blocks.meta} theme={theme} />}

              {/* Actions */}
              {blocks.actions && <ActionsBlock actions={blocks.actions} theme={theme} />}
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default FeaturedLayout;
